import type { Tokens, TokenizerAndRendererExtension } from 'marked';

// Tables that lost their delimiter row on the way out of a slide deck.
//
// The lesson bodies were converted from decks, and the conversion dropped two
// things from every multi-column block: the `| --- | --- |` delimiter row that
// GFM requires, and the line spacing, so each row ended up separated by a blank
// line. What is left is not a table to any Markdown parser. It is a run of
// one-line paragraphs that happen to contain pipes, and it renders as exactly
// that: `| Single-AZ | 1x infra | one zone fails → you're down |` as literal
// text across the page.
//
// It is not rare. Scanning the four course corpora on 3 Sep 2026, 70 lesson
// files carry at least one of these, including every table in Module 1 through
// Module 8 of the senior-to-staff course and every one in llms-and-agents. A
// paying customer reported it on 30 Aug, was told a different instance of it had
// been fixed, and reported on 3 Sep that the tables were still wrong.
//
// Fixing it here rather than in 164 markdown files is deliberate. Editing the
// files fixes today's corpus and leaves the next deck conversion to reintroduce
// it, and it would also need a content re-import to reach production. This
// reaches production with a frontend deploy and covers content that has not been
// written yet.
//
// NO HEADER IS INVENTED. Some of these blocks open with what is plainly a header
// row and some open with data, and there is no rule that tells them apart
// without guessing. A guess that is wrong renders a data row in header styling,
// which is the renderer asserting something the source never said. So every row
// is a body row: each cell lands in its right column, which is the whole
// complaint, and nothing is claimed that the markdown did not claim. Authoring a
// real GFM table with a delimiter row still gets a real header, and that path is
// untouched.

/** A pipe row: starts with `|`, holds at least one more, and is one line. */
const ROW = /^[ \t]{0,3}\|.*\|[ \t]*$/;

/** A GFM delimiter row. Its presence means this is a real table; leave it alone. */
const DELIMITER = /^[ \t]{0,3}\|?[ \t]*:?-{1,}:?[ \t]*(\|[ \t]*:?-{1,}:?[ \t]*)+\|?[ \t]*$/;

/**
 * Split a pipe row into cells, GFM-style.
 *
 * Outer pipes are stripped, and an escaped pipe stays inside its cell rather
 * than splitting it: several of these rows carry `a \| b` in a code span.
 */
function cells(row: string): string[] {
  const trimmed = row.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed
    .split(/(?<!\\)\|/)
    .map((c) => c.trim().replace(/\\\|/g, '|'));
}

export interface LooseTableToken extends Tokens.Generic {
  type: 'looseTable';
  rows: { tokens: Tokens.Generic[] }[][];
}

/**
 * Build the extension.
 *
 * `renderRow` is injected so the styling lives with the other renderers rather
 * than being a second copy of the same Tailwind classes that can drift from the
 * real table's.
 */
export function looseTableExtension(
  renderTable: (rows: string[][]) => string,
): TokenizerAndRendererExtension {
  return {
    name: 'looseTable',
    level: 'block',
    // Cheap pre-check so marked only calls the tokenizer near a candidate.
    start(src: string) {
      const i = src.search(/^[ \t]{0,3}\|/m);
      return i === -1 ? undefined : i;
    },
    tokenizer(src: string) {
      const lines = src.split('\n');
      const collected: string[] = [];
      let consumed = 0;
      let i = 0;

      while (i < lines.length) {
        const line = lines[i]!;
        if (ROW.test(line)) {
          // A delimiter anywhere in the run means GFM can parse this itself, and
          // marked's own table tokenizer will do it better. Bail entirely rather
          // than half-claiming the block.
          if (DELIMITER.test(line)) return undefined;
          collected.push(line);
          consumed = i + 1;
          i++;
          continue;
        }
        // A single blank line between rows is the conversion artefact and is
        // skipped. Two blank lines, or any other content, ends the block: that
        // is a real paragraph break and two separate tables must not be merged.
        if (line.trim() === '' && i + 1 < lines.length && ROW.test(lines[i + 1]!)) {
          i++;
          continue;
        }
        break;
      }

      // One pipe row on its own is far more likely to be prose than a table.
      if (collected.length < 2) return undefined;

      const raw = lines.slice(0, consumed).join('\n');
      const rows = collected.map((row) =>
        cells(row).map((text) => ({ tokens: this.lexer.inlineTokens(text) })),
      );
      return { type: 'looseTable', raw, rows } as LooseTableToken;
    },
    renderer(token) {
      const t = token as LooseTableToken;
      return renderTable(
        t.rows.map((row) => row.map((cell) => this.parser.parseInline(cell.tokens as never))),
      );
    },
  };
}
